/**
 * MultiSearchCombobox — P0-B Sub-Step 2d
 *
 * Multi-select sibling of `SearchCombobox`. Built on the same Radix
 * `Popover` + `cmdk` foundation so the two components behave consistently
 * in cascading filter UIs.
 *
 *   <MultiSearchCombobox
 *     value={["bmw", "audi"]}
 *     onChange={setValue}
 *     options={[{ value: "bmw", label: "BMW" }, ...]}
 *     placeholder="Marken wählen…"
 *     dataTestId="brand-multi"
 *   />
 *
 * Semantics:
 *   - `value` is `string[]`.
 *   - `onChange(nextList)` is fired with the next array (immutable copy).
 *   - Selected items render as removable Chips above (or inside) the
 *     trigger. Each Chip has an X-button — keyboard accessible.
 *   - Popover stays OPEN after a select so the user can multi-pick.
 *   - "Clear all" footer link clears `value`.
 *   - All ARIA + keyboard nav semantics inherited from cmdk.
 *
 * TC4 Recovery Iteration 2 — canonical-validation predicate is a
 * tri-state (true / false / null=unknown) and supports an explicit
 * `canonicalValues` reference list separate from `options`. This lets
 * a caller cascade-filter the dropdown while keeping the full
 * canonical universe for validation. Matching is case-insensitive so
 * a backend that returns "3ER" matches a stored "3er" pref.
 *
 * Re-use targets:
 *   - V2 Preferences: makes / models / model_series / postal_codes
 *   - Future: Watchlists, Watches/Boats verticals
 *   - Mobile parity in a later Expo sprint (RN port).
 */
import * as React from "react";
import { AlertTriangle, Check, ChevronsUpDown, X, Loader2 } from "lucide-react";

import i18n from "../../i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Internal helper — kept local to avoid an extra import cycle with
// `canonicalResolver`. Lower-cases + trims + collapses whitespace so
// e.g. backend "3ER" == user pref "3er".
function normalize(s) {
  if (s == null) return "";
  return String(s).toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Tri-state canonical check.
 *   true   → value is definitely in the canonical universe
 *   false  → value is definitely NOT canonical (render warn pill)
 *   null   → unknown (loading / no reference list yet) → render neutral
 *
 * Precedence:
 *   1. `loading=true` → always `null` (no flicker during fetch).
 *   2. Explicit `canonicalValues` prop (array, even if empty) → strict
 *      check against this set, case-insensitive.
 *   3. `options` array of >0 entries → derive set from `options[].value`,
 *      case-insensitive.
 *   4. Neither → `null` (unknown, cascade-locked or pre-fetch).
 *
 * @param {string} value
 * @param {string[] | null | undefined} canonicalValues
 * @param {{value: any}[] | null | undefined} options
 * @param {boolean} loading
 * @returns {boolean | null}
 */
function checkCanonical(value, canonicalValues, options, loading) {
  if (loading) return null;
  const n = normalize(value);
  if (Array.isArray(canonicalValues)) {
    // Caller passed an explicit reference list — trust it strictly.
    // If it's empty we still consider the universe known (the caller
    // is asserting "nothing is canonical here", e.g. country mismatch).
    if (canonicalValues.length === 0) return false;
    for (const cv of canonicalValues) {
      if (normalize(cv) === n) return true;
    }
    return false;
  }
  if (Array.isArray(options) && options.length > 0) {
    for (const o of options) {
      if (normalize(o?.value) === n) return true;
    }
    return false;
  }
  // No reference at all → unknown.
  return null;
}

/**
 * @param {Object} props
 * @param {string[]} props.value
 * @param {(v: string[]) => void} props.onChange
 * @param {{value: string, label: string, count?: number, hint?: string}[]} props.options
 * @param {string[]} [props.canonicalValues]
 *   Optional explicit canonical reference list. When provided, chip
 *   validation is performed against THIS set instead of deriving it
 *   from `options[].value`. Use this when the dropdown is cascade-
 *   filtered (e.g. PLZ by Bundesland) but the canonical universe
 *   spans more than the currently visible options.
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledReason]
 * @param {boolean} [props.loading]
 * @param {string} [props.emptyMessage]
 * @param {number} [props.maxVisible]
 * @param {string} [props.className]
 * @param {string} [props.dataTestId]
 */
export function MultiSearchCombobox({
  value = [],
  onChange,
  options = [],
  canonicalValues,
  placeholder,
  disabled = false,
  disabledReason = null,
  loading = false,
  emptyMessage,
  maxVisible = 500,
  className,
  dataTestId,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = React.useMemo(() => new Set(value || []), [value]);

  // Resolved option list keyed by value so chips can render labels.
  // Lookup is by normalized key so a case-mismatched stored value
  // (e.g. "3er") still picks up the canonical label "3ER".
  const optionsByValue = React.useMemo(() => {
    const m = new Map();
    for (const o of options) {
      if (o?.value != null) m.set(normalize(o.value), o);
    }
    return m;
  }, [options]);

  const truncated = options.length > maxVisible;
  const visible = truncated ? options.slice(0, maxVisible) : options;

  const toggle = (v) => {
    const next = new Set(selectedSet);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
    // Keep popover open so the user can multi-pick rapidly.
  };

  const removeOne = (e, v) => {
    e.stopPropagation();
    onChange((value || []).filter((x) => x !== v));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className={cn("space-y-2", className)} data-testid={dataTestId ? `${dataTestId}-wrap` : undefined}>
      {/* Selected chips ------------------------------------------------ */}
      {value && value.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={dataTestId ? `${dataTestId}-chips` : undefined}
        >
          {value.map((v) => {
            const opt = optionsByValue.get(normalize(v));
            const label = opt?.label ?? v;
            // Tri-state predicate — see `checkCanonical()` above.
            //   true   → canonical
            //   false  → definitely non-canonical → warn pill
            //   null   → unknown (loading or no reference) → render
            //            neutral so we don't false-flag during fetch.
            const canonical = checkCanonical(v, canonicalValues, options, loading);
            const isWarn = canonical === false;
            const chipTestId = dataTestId
              ? `${dataTestId}-chip-${v}`
              : undefined;
            const warnTestId = dataTestId
              ? `${dataTestId}-chip-warn-${v}`
              : undefined;
            return (
              <span
                key={v}
                data-testid={chipTestId}
                data-canonical={isWarn ? "false" : "true"}
                title={
                  isWarn
                    ? i18n.t("common:ui.notCanonical", { value: v })
                    : undefined
                }
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition",
                  isWarn
                    ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15 hover:border-amber-500/50",
                )}
              >
                {isWarn && (
                  <AlertTriangle
                    className="h-3 w-3"
                    aria-hidden="true"
                    data-testid={warnTestId}
                  />
                )}
                <span className="truncate max-w-[14rem]">{label}</span>
                <button
                  type="button"
                  aria-label={`Entferne ${label}`}
                  data-testid={dataTestId ? `${dataTestId}-chip-remove-${v}` : undefined}
                  onClick={(e) => removeOne(e, v)}
                  className={cn(
                    "ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full transition focus:outline-none focus:ring-1",
                    isWarn
                      ? "text-rose-300 hover:bg-rose-500/20 hover:text-rose-100 focus:ring-rose-300"
                      : "text-amber-200/70 hover:bg-amber-500/20 hover:text-amber-100 focus:ring-amber-400",
                  )}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            data-testid={dataTestId ? `${dataTestId}-clear-all` : undefined}
            onClick={clearAll}
            className="text-[11px] text-zinc-500 hover:text-amber-300 underline-offset-2 hover:underline transition"
          >
            {i18n.t("common:ui.clearAll")}
          </button>
        </div>
      )}

      {/* Trigger + Popover -------------------------------------------- */}
      <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-disabled={disabled}
            disabled={disabled}
            title={disabled && disabledReason ? disabledReason : undefined}
            className={cn(
              "w-full h-10 justify-between font-normal rounded-lg",
              "border-zinc-800 bg-zinc-950/60 text-zinc-200",
              "hover:bg-zinc-900 hover:border-zinc-700 hover:text-zinc-100",
              "focus-visible:ring-2 focus-visible:ring-amber-500/50 focus-visible:border-amber-500/40",
              "data-[state=open]:border-amber-500/40 data-[state=open]:ring-2 data-[state=open]:ring-amber-500/30",
              "disabled:opacity-50 disabled:hover:bg-zinc-950/60 disabled:hover:border-zinc-800",
              "transition",
            )}
            data-testid={dataTestId}
          >
            <span className={cn(
              "truncate text-left flex-1",
              value && value.length > 0 ? "text-zinc-200" : "text-zinc-500",
            )}>
              {loading ? (
                <span className="inline-flex items-center gap-2 text-zinc-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {i18n.t("common:ui.loading")}
                </span>
              ) : value && value.length > 0 ? (
                `${i18n.t("common:ui.selectedCount", { count: value.length })} — ${placeholder || i18n.t("common:ui.select")}`
              ) : (
                placeholder || i18n.t("common:ui.select")
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-[--radix-popover-trigger-width] min-w-[220px] p-0 overflow-hidden",
            "rounded-xl border border-zinc-800 bg-zinc-950 text-zinc-100",
            "shadow-2xl shadow-black/60",
          )}
          align="start"
          sideOffset={6}
          data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
        >
          <Command className="bg-transparent text-zinc-100">
            <div className="border-b border-zinc-800 [&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]_svg]:text-zinc-500">
              <CommandInput
                placeholder={`${i18n.t("common:ui.search")}…`}
                className="text-zinc-100 placeholder:text-zinc-500 bg-transparent"
                data-testid={dataTestId ? `${dataTestId}-input` : undefined}
              />
            </div>
            <CommandList className="dark-scroll max-h-[280px] py-1">
              <CommandEmpty className="py-6 text-center text-sm text-zinc-500">
                {emptyMessage || i18n.t("common:ui.noResults")}
              </CommandEmpty>
              <CommandGroup className="px-1 py-0.5">
                {visible.map((opt) => {
                  const isSelected = selectedSet.has(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={`${opt.label} ${opt.value}`}
                      onSelect={() => toggle(opt.value)}
                      data-testid={
                        dataTestId ? `${dataTestId}-option-${opt.value}` : undefined
                      }
                      className={cn(
                        "rounded-md px-2.5 py-2 my-0.5 text-sm cursor-pointer",
                        "text-zinc-300",
                        "data-[selected=true]:bg-amber-500/15 data-[selected=true]:text-amber-100",
                        "aria-selected:bg-amber-500/15 aria-selected:text-amber-100",
                        isSelected && "bg-amber-500/10 text-amber-100",
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 text-amber-400",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="ml-2 text-xs text-zinc-500 truncate">
                          {opt.hint}
                        </span>
                      )}
                      {typeof opt.count === "number" && (
                        <span className="ml-2 text-xs text-zinc-500">
                          {opt.count.toLocaleString("de-AT")}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {truncated && (
                <div
                  className="border-t border-zinc-800 px-3 py-2 text-xs text-zinc-500"
                  data-testid={
                    dataTestId ? `${dataTestId}-truncation-hint` : undefined
                  }
                >
                  +{(options.length - maxVisible).toLocaleString("de-AT")} weitere
                  — bitte suchen um zu verfeinern.
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export const __test__ = { checkCanonical, normalize };

export default MultiSearchCombobox;
